// Wizard v9 (Geno, 2026-10-09) — the CIRCLE SCOPE shared by the circle-scoped caster layers (spells_cannot_be_counterspelled,
// spell_mana_discount, aoe_spells_spare_allies, scroll_spell_damage_multiplier). A provider item that also carries
// `if_circle_of_magic_cast` limits its layer to spells whose `spell_circle` is one of the listed circles; a provider without it
// applies to every spell. Pass it as casterLayerProvider()'s `matches` predicate so a scoped provider that does not match is
// skipped and a second, matching provider can still be found.
import { type Row } from './context.js';

/** The minimal reader surface (AbilityPreconditionReaders and JanusCtx['read'] both satisfy it). */
export interface CircleScopeReaders {
  value(row: Row, key: string): unknown;
  entries?(row: Row, key: string): readonly unknown[];
}

function uuids(read: CircleScopeReaders, row: Row, key: string): string[] {
  const raw = read.entries ? read.entries(row, key) : [read.value(row, key)];
  const out: string[] = [];
  for (const entry of raw) {
    const id = typeof entry === 'string' ? entry
      : entry && typeof entry === 'object' ? String((entry as Record<string, unknown>).item_id ?? (entry as Record<string, unknown>).item_layer_effect ?? '') : '';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id.trim())) out.push(id.trim().toUpperCase());
  }
  return out;
}

/** The spell's own circles (`spell_circle` is single-valued in the catalog; read as a list to tolerate a carrier union). */
export function spellCircles(read: CircleScopeReaders, spell: Row): string[] {
  return uuids(read, spell, 'spell_circle');
}

/** True when `provider`'s layer reaches `spell`: no if_circle_of_magic_cast on the provider, or the spell's circle is listed. */
export function spellInCircleScope(read: CircleScopeReaders, provider: Row, spell: Row): boolean {
  const scope = uuids(read, provider, 'if_circle_of_magic_cast');
  if (scope.length === 0) return true;
  const circles = spellCircles(read, spell);
  return circles.some(circle => scope.includes(circle));
}
